import { Api } from "../api/Api";
import { paramIds } from "../api/ApiUtil";
import { AbandonedVehicle, AbandonedVehicleRun, AerialImage } from "../api/Application";
import { LoaderFunctionArgs } from "react-router-dom";
import type { Feature, FeatureCollection, GeoJSON, Geometry, LineString, Polygon } from "geojson";
import { AbandonedVehicleSmall, expandAbandonedVehicle } from "./abandonedVehicleCompressed";
import { latLngToPixel } from "../common/Geography";
import { areaCodes, DeckColor } from "../AbandonedVehicleListPageUtil";
import { fileUrl } from "../api/Url";

export interface Match extends AbandonedVehicleSmall {};

export interface AreaJsonProps {
    "@id":       string;
    admin_level: string;
    boundary:    string;
    name:        string;
    source:      string;
    type:        string;
}

export interface AreaProps {
    id:    number;
    name:  string;
    color: DeckColor;
}

export interface RoadProps {
    name: string;
}

export interface AbandonedVehicleLoaderData {
    runId:       number;
    imageA:      AerialImage;
    imageB:      AerialImage;
    vehicles:    Match[];
    savedOffset: [number, number];
    areas:       FeatureCollection<Polygon,    AreaProps>;
    roads:       FeatureCollection<LineString, RoadProps>;
}

// recursively transform coords at any nesting depth
// handles Polygon ([rings][points][xy]), MultiPolygon ([polys][rings][points][xy]), etc.
function transformCoords( coords: any, image: AerialImage ): any {
    if( typeof coords[0] === "number" ) {
        // leaf: [lng, lat]
        const [lng, lat] = coords;
        return latLngToPixel( lat, lng, image );
    }
    return coords.map( ( c: any ) => transformCoords( c, image ) );
}

export async function abandonedVehicleOnlyLoader( { params }: LoaderFunctionArgs ): Promise<AbandonedVehicle> {
    const { vehicleId } = paramIds( params );
    const res = await Api.abandonedVehicleGet( vehicleId );
    if( !res.isOk ) {
        throw new Error( `Unable to load abandoned vehicle ${vehicleId}` );
    }
    return res.value;
}

export async function abandonedVehicleLoader( { params }: LoaderFunctionArgs ): Promise<AbandonedVehicleLoaderData> {
    const { runId } = paramIds( params );
    const [run, result, offset, areaResp, roadsResp] = await Promise.all( [
        Api.abandonedVehicleRunGet( runId ),
        Api.abandonedVehicleRunVehicleList( runId ),
        Api.aerialImagePairOffsetGet( runId ),
        fetch( fileUrl( "external/pa-la/areas.json" ) ),
        fetch( fileUrl( "external/pa-la/roads.json" ) ),
    ] );

    if( !run.isOk    ) throw new Error( "Failed to fetch run." );
    if( !result.isOk ) throw new Error( result.error || "Failed to fetch abandoned vehicles." );
    if( !offset.isOk ) throw new Error( offset.error || "Failed to fetch image offset." );

    const { before, after } = run.value;
    let areas: FeatureCollection<Polygon, AreaProps> | undefined;
    if( areaResp.ok ) {
        const raw: FeatureCollection<Polygon, AreaJsonProps> = await areaResp.json();
        raw.features = raw.features.filter( (f: any) => f.geometry.type !== "Point" );
        areas = {
            ...raw,
            features: raw.features.map( ( f: Feature<Polygon, AreaJsonProps> ) => {
                const area = areaCodes.find( a => a.name === f.properties.name );
                if( area === undefined ) {
                    throw new Error( `${f.properties.name} not found in AreaCodes collection` );
                }
                return ( {
                    ...f,
                    properties: {
                        // ...f.properties,
                        id:    area.id,
                        name:  area.name,
                        color: area.color,
                    },
                    geometry: {
                        ...f.geometry,
                        coordinates: transformCoords( f.geometry.coordinates, after ),
                    },
                } );
            } ),
        };
    }

    let roads: FeatureCollection<LineString, RoadProps> | undefined;
    if( roadsResp.ok ) {
        const raw = await roadsResp.json() as FeatureCollection<Geometry, RoadProps>;
        roads = {
            ...raw,
            features: raw.features.map( ( f: any ) => ( {
                ...f,
                geometry: {
                    ...f.geometry,
                    coordinates: transformCoords( f.geometry.coordinates, after ),
                },
            } ) ),
        };
    }

    const { offsetX, offsetY } = offset.value;
    return {
        runId:       runId,
        imageA:      before,
        imageB:      after,
        vehicles:    result.value.map( expandAbandonedVehicle ) as Match[],
        savedOffset: [offsetX, offsetY], //-9, -5
        areas:       areas!,
        roads:       roads!
    };
}

